import { z } from 'zod';
/**
 * The formatted text Account Users write for custom data: a small, fixed
 * document tree (the dashboard's editor produces it), never HTML. The API
 * accepts only the nodes and marks listed here, so stored text can't carry
 * markup, scripts or styles; the dashboard renders it through the editor, and
 * the assistant reads it as Markdown (`richTextToMarkdown`).
 */
export const RICH_TEXT_NODE_TYPES = [
    'doc',
    'paragraph',
    'heading',
    'text',
    'bulletList',
    'orderedList',
    'listItem',
    'blockquote',
    'hardBreak',
    'horizontalRule',
];
export const RICH_TEXT_MARK_TYPES = ['bold', 'italic', 'underline', 'strike', 'link'];
/** The most characters of text one custom page may hold. */
export const MAX_CUSTOM_PAGE_TEXT = 5_000;
/**
 * A bound on one text node, only so a request can't be huge: the page's own
 * limit (`MAX_CUSTOM_PAGE_TEXT`) is checked on the whole text, with a message
 * the form shows.
 */
const MAX_TEXT_NODE = 50_000;
const linkHref = z
    .string()
    .max(2048)
    .refine((href) => /^(https?:\/\/|mailto:|tel:)/i.test(href), {
    message: 'A link must start with https://, http://, mailto: or tel:.',
});
export const richTextMarkSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('bold') }),
    z.object({ type: z.literal('italic') }),
    z.object({ type: z.literal('underline') }),
    z.object({ type: z.literal('strike') }),
    z.object({
        type: z.literal('link'),
        attrs: z.object({ href: linkHref }).loose(),
    }),
]);
export const richTextNodeSchema = z.lazy(() => z
    .object({
    type: z.enum(RICH_TEXT_NODE_TYPES),
    attrs: z
        .object({
        level: z.number().int().min(1).max(4).optional(),
        start: z.number().int().min(0).max(100_000).optional(),
    })
        .loose()
        .optional(),
    content: z.array(richTextNodeSchema).max(5_000).optional(),
    marks: z.array(richTextMarkSchema).max(10).optional(),
    text: z.string().max(MAX_TEXT_NODE).optional(),
})
    .transform((node) => ({
    type: node.type,
    // Only the attributes the document uses are kept.
    ...(node.attrs?.level !== undefined || node.attrs?.start !== undefined
        ? {
            attrs: {
                ...(node.attrs.level !== undefined ? { level: node.attrs.level } : {}),
                ...(node.attrs.start !== undefined ? { start: node.attrs.start } : {}),
            },
        }
        : {}),
    ...(node.content !== undefined ? { content: node.content } : {}),
    ...(node.marks !== undefined
        ? {
            marks: node.marks.map((mark) => mark.type === 'link'
                ? { type: 'link', attrs: { href: mark.attrs.href } }
                : mark),
        }
        : {}),
    ...(node.text !== undefined ? { text: node.text } : {}),
})));
/** A whole document: a `doc` node at the root. */
export const richTextDocumentSchema = richTextNodeSchema.refine((node) => node.type === 'doc', {
    message: 'The content must be a document.',
});
/** The text a document holds, without formatting: for "has it any content?" and previews. */
export function richTextToPlainText(node) {
    if (node.type === 'text')
        return node.text ?? '';
    if (node.type === 'hardBreak')
        return '\n';
    const inner = (node.content ?? []).map(richTextToPlainText);
    const isBlockContainer = ['doc', 'bulletList', 'orderedList', 'listItem', 'blockquote'].includes(node.type);
    return isBlockContainer ? inner.join('\n') : inner.join('');
}
function inlineMarkdown(nodes) {
    return nodes
        .map((node) => {
        if (node.type === 'hardBreak')
            return '  \n';
        if (node.type !== 'text')
            return '';
        let text = node.text ?? '';
        const marks = node.marks ?? [];
        if (marks.some((m) => m.type === 'bold'))
            text = `**${text}**`;
        if (marks.some((m) => m.type === 'italic'))
            text = `*${text}*`;
        if (marks.some((m) => m.type === 'strike'))
            text = `~~${text}~~`;
        const link = marks.find((m) => m.type === 'link');
        if (link?.type === 'link')
            text = `[${text}](${link.attrs.href})`;
        return text;
    })
        .join('');
}
function blockMarkdown(node, indent) {
    const children = node.content ?? [];
    switch (node.type) {
        case 'doc':
            return children
                .map((child) => blockMarkdown(child, indent))
                .filter(Boolean)
                .join('\n\n');
        case 'paragraph':
            return indent + inlineMarkdown(children);
        case 'heading': {
            const level = Math.min(4, Math.max(1, Number(node.attrs?.['level'] ?? 2)));
            return `${indent}${'#'.repeat(level)} ${inlineMarkdown(children)}`;
        }
        case 'bulletList':
        case 'orderedList': {
            const start = Number(node.attrs?.['start'] ?? 1);
            return children
                .map((item, index) => {
                const bullet = node.type === 'bulletList' ? '- ' : `${start + index}. `;
                const body = (item.content ?? [])
                    .map((child) => blockMarkdown(child, `${indent}   `).trimStart())
                    .join(`\n${indent}   `);
                return `${indent}${bullet}${body}`;
            })
                .join('\n');
        }
        case 'blockquote':
            return children
                .map((child) => blockMarkdown(child, ''))
                .join('\n\n')
                .split('\n')
                .map((line) => `${indent}> ${line}`)
                .join('\n');
        case 'horizontalRule':
            return `${indent}---`;
        default:
            return '';
    }
}
/** The document as Markdown, which is how the assistant reads custom data. */
export function richTextToMarkdown(document) {
    return blockMarkdown(document, '').trim();
}
//# sourceMappingURL=rich-text.js.map